/**
 * Platform-agnostic debug logging sink.
 *
 * ClientApp-Shared must not depend on platform APIs (window/localStorage) or
 * on Node-only packages that break React Native's Metro bundler, so it never
 * imports the `debug` npm package directly. Instead, each host app (web,
 * mobile) injects its own logger factory via `setDebugLoggerFactory` — on web
 * that's typically backed by `debug`, gated by `localStorage.debug`. Hosts
 * that never call `setDebugLoggerFactory` get a silent no-op logger.
 */
export type DebugLogFn = (message: string, ...args: unknown[]) => void;
export type DebugLoggerFactory = (scope: string) => DebugLogFn;

const noopLogFn: DebugLogFn = () => {};
const noopFactory: DebugLoggerFactory = () => noopLogFn;

let activeFactory: DebugLoggerFactory = noopFactory;

/**
 * Called once by a host app at startup to route debug logs through its own
 * platform-specific logger (e.g. the `debug` package on web).
 */
export const setDebugLoggerFactory = (factory: DebugLoggerFactory | null): void => {
  activeFactory = factory ?? noopFactory;
};

/**
 * Returns a logger for the given scope. Resolution of the underlying factory
 * is deferred to each call so this can safely be assigned at module scope
 * before the host app has called `setDebugLoggerFactory`.
 */
export const getDebugLogger = (scope: string): DebugLogFn => {
  return (message, ...args) => {
    activeFactory(scope)(message, ...args);
  };
};
